/* eslint-disable react-hooks/exhaustive-deps */
import constate from 'constate'
import { useEffect, useState } from 'react'

import { IOrganization } from './useOrganization'
import { MapState } from './useProjectDetails'
import { IFile, useWorkspaceContext } from './useWorkspace'
import { useZippieIdContext } from './useZippieId'
import apiClient from '../api/apiClient'

interface IDate {
  endDate?: Date | null
  startDate?: Date | null
  continuos?: boolean
}

export interface IIcon {
  id: number
  name: string
  link: string
}

interface Subscription {
  plan: string
  features: IFeature[]
}

interface IFeature {
  scope: string
  count: {
    min: number
    max: number
    current: number
  }
}

interface IEmployee {
  _id: string
  _pictureId?: string
  displayName?: string
  phoneNumber?: string
  location?: string
  description?: string
  picture?: IFile
  roles?: string[] | null
}

export interface IProjectOrganization {
  role: string
  employee: IEmployee
  _organizationId: string
  _employeeId: string | undefined
  organization: IOrganization
}

export interface IProject {
  _pictureId?: string
  playground?: boolean
  name?: string
  status?: string
  id?: string
  state?: string
  description?: string
  thumbnail?: IFile
  projectDates?: IDate
  icon?: IIcon
  dataset?: {
    enabled: boolean
    items: [
      {
        _id: string
        _datasetId: string
        type: string
        cid: string
        status: string
      },
    ]
  }
  nft?: {
    contractAddress: string
    contractDeployed: string
    contractOwner: string
    contractId: string
    enabled: boolean
    _contractId: string
  }
  _locationId?: string[]
  country?: string
  project_manager?: string
  tags?: string[]
  story?: string
  metric?: {
    value: string
    unit: string
    verb: string
  }
  subscription?: Subscription
  organizationId?: Array<string>
  _organizationId?: Array<string>
  organizations?: IProjectOrganization[]
  mapView?: MapState
}

export interface ICreateProjectResponse {
  data: {
    data: {
      nodeId: string
      name: string
      status: string
      data: {
        name: string
        description: string
      }
      created_by: number
      _id: string
    }
  }
}

interface IGetProjectResponse {
  _id: string
  _workspaceId: number
  name: string
  status: string
  state: string
  description: string
  _pictureId: string
  country?: string
  projectDates: IDate
  metric?: {
    value: string
    unit: string
    verb: string
  }
  story: string
  _projectManagerId: string[]
  picture?: {
    url: string
  }
  subscription?: Subscription
  organizationId?: Array<string>
  _organizationId?: Array<string>
  organizations?: IProjectOrganization[]
}

export interface IGetAllProjectsResponse {
  data: IGetProjectResponse[]
}

export const useProjects = () => {
  const [projectCount, setProjectCount] = useState(0)
  const { isLoggedIn } = useZippieIdContext()

  const {
    selectedNodeId,
    uploadFile,
    updateSelectedWorkspace,
    isWorkspaceFetchingInProgress,
    selectedWorkspaceDetails,
  } = useWorkspaceContext()

  const [queryOptions, setQueryOption] = useState({
    sortBy: 'createdAt,-1',
    skip: 0,
    limit: 8,
    status: '',
    query: '',
  })

  const [projects, setProjects] = useState<IProject[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const getActiveProjects = () => projects.filter((project) => project.status !== 'archived')
  const fetchProjects = async () => {
    setIsLoading(true)

    const adjustedStatus = queryOptions.status === 'archived' ? '' : 'active,draft'

    const { data } = await apiClient.get(`/workspace/${selectedNodeId}/project`, {
      params: {
        ...queryOptions,
        status: adjustedStatus,
      },
    })
    setProjectCount(data.count)
    const projects = (data as IGetAllProjectsResponse).data.map((project) => ({
      id: project._id,
      thumbnail: {
        id: project._pictureId,
        url: project.picture?.url,
      },
      ...project,
    }))
    setProjects(projects)
    setIsLoading(false)
  }

  useEffect(() => {
    if (selectedNodeId && !isWorkspaceFetchingInProgress && selectedWorkspaceDetails.subscription?.data) {
      fetchProjects()
    }
  }, [selectedNodeId, isWorkspaceFetchingInProgress, queryOptions, selectedWorkspaceDetails])

  const deleteProjectById = async (projectId: string) => {
    const { data } = await apiClient.delete(`/workspace/${selectedNodeId}/project/${projectId}`)
    await fetchProjects()
    await updateSelectedWorkspace()
    return data
  }
  const addNewProject = async (projectDetails: IProject, thumbnail?: File | null | undefined) => {
    let file
    if (thumbnail) {
      file = await uploadFile(thumbnail, selectedNodeId)
    }

    const { data } = await apiClient.post<any, ICreateProjectResponse>(`/workspace/${selectedNodeId}/project`, {
      status: 'draft',
      name: projectDetails.name,
      description: projectDetails.description,
      _pictureId: file?.id,
      playground: projectDetails.playground,
    })
    await updateSelectedWorkspace()

    await fetchProjects()
    return data.data
  }

  const updateProject = async (projectDetails: any) => {
    const { data } = await apiClient.patch(`/workspace/${selectedNodeId}/project/${projectDetails.id}`, projectDetails)
    await fetchProjects()
    return data
  }
  const onQueryChange = (value: string, name: string) => {
    if (value !== 'archived') {
      setQueryOption({ ...queryOptions, [name]: value, status: '' })
      return
    }
    setQueryOption({ ...queryOptions, [name]: value })
  }
  const createPlayground = async () =>
    addNewProject({
      name: 'Playground',
      description: 'This is a playground project for you to quickly explore AirImpact’s features.',
      playground: true,
    })
  return {
    projects,
    addNewProject,
    deleteProjectById,
    updateProject,
    getActiveProjects,
    fetchProjects,
    isLoading,
    setQueryOption,
    queryOptions,
    onQueryChange,
    projectCount,
    createPlayground,
  }
}

const [ProjectsContainer, useProjectsContext] = constate(useProjects)

export { ProjectsContainer, useProjectsContext }
